import React, { useEffect, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api, {
  API_BASE,
  getAuthToken,
  normalizeListResponse,
  resolveAssetUrl
} from '../utils/api';
import ConfirmModal from '../components/ConfirmModal';
import ProductGalleryEditor from '../components/ProductGalleryEditor';
import { coverImage, galleryFromProduct } from '../utils/productImages';
import { ANGLE_ORDER } from '../utils/imageAngles';

export default function Products() {

  const {
    token,
    loading,
    isAuthenticated
  } = useAuth();

  const [products, setProducts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);

  const emptyForm = {
    id: '',
    name: '',
    short_name: '',
    price: '60',
    badge: 'Premium fitted',
    gallery: [],
    description: '',
    sizes: '',
    is_active: true
  };

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState('');
  const [listLoading, setListLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteCloudinaryOnSave, setDeleteCloudinaryOnSave] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const originalGalleryRef = useRef([]);

  useEffect(() => {

    if (loading) {
      return;
    }

    if (!isAuthenticated || !token) {
      setListLoading(false);
      return;
    }

    let cancelled = false;

    const loadProducts = async () => {

      setListLoading(true);
      setLoadError('');

      try {

        const data = await api.get('/products', token || getAuthToken());

        if (!cancelled) {
          setProducts(normalizeListResponse(data));
        }

      } catch (error) {

        console.error(
          'Products load error:',
          error
        );

        if (!cancelled) {
          setProducts([]);
          setLoadError(error.message || 'Failed to load products');
        }

      } finally {

        if (!cancelled) {
          setListLoading(false);
        }

      }

    };

    loadProducts();

    return () => {
      cancelled = true;
    };

  }, [token, loading, isAuthenticated]);

  function startEdit(product) {

    setEditingId(product.id);

    const gallery = galleryFromProduct(product);
    originalGalleryRef.current = [...gallery];

    setForm({
      name: product.name || '',
      short_name: product.short_name || '',
      price: product.price || '',
      badge: product.badge || '',
      gallery,
      description: product.details?.description || product.description || '',
      sizes: product.sizes || '',
      is_active: product.is_active
    });

  }

  function closeConfirmModal() {
    setConfirmModal(null);
  }

  async function cleanupRemovedImages(previousGallery, nextGallery) {
    const removedUrls = previousGallery.filter(
      (url) => url && !nextGallery.includes(url)
    );

    if (!removedUrls.length || !deleteCloudinaryOnSave) {
      return;
    }

    try {
      await api.post(
        '/products/media/delete',
        {
          urls: removedUrls,
          deleteFromCloudinary: true
        },
        token || getAuthToken()
      );
    } catch (error) {
      console.error('Cloudinary cleanup warning:', error);
      setStatus(
        (current) =>
          `${current}${current ? ' ' : ''}(Gallery saved; some Cloudinary cleanup failed.)`
      );
    }
  }

  async function setProductActive(product, isActive) {
    setSaving(true);
    setStatus('');

    try {
      await api.patch(
        `/products/${product.id}`,
        { is_active: isActive },
        token || getAuthToken()
      );

      setProducts((prev) =>
        prev.map((item) =>
          item.id === product.id ? { ...item, is_active: isActive } : item
        )
      );

      if (editingId === product.id) {
        setEditingId(null);
      }

      setStatus(
        isActive
          ? `"${product.name}" is active on the storefront again.`
          : `"${product.name}" was hidden from the storefront.`
      );
    } catch (error) {
      console.error(error);
      setStatus(error.message || 'Failed to update product visibility');
    } finally {
      setSaving(false);
      closeConfirmModal();
    }
  }

  function requestDeactivateProduct(product) {
    setConfirmModal({
      title: 'Hide product from storefront?',
      message: `"${product.name}" will be deactivated (is_active = false). It stays in admin and order history. Inventory is preserved. You can reactivate it later.`,
      confirmLabel: 'Hide product',
      destructive: true,
      onConfirm: () => setProductActive(product, false)
    });
  }

  function requestReactivateProduct(product) {
    setConfirmModal({
      title: 'Show product on storefront?',
      message: `"${product.name}" will become visible to customers again.`,
      confirmLabel: 'Activate product',
      destructive: false,
      onConfirm: () => setProductActive(product, true)
    });
  }

  function productPayload() {
    const gallery = Array.isArray(form.gallery) ? form.gallery.filter(Boolean) : [];

    return {
      name: form.name,
      short_name: form.short_name,
      price: form.price,
      badge: form.badge,
      gallery,
      front_image: coverImage(gallery),
      description: form.description,
      is_active: form.is_active,
      sizes: form.sizes
    };
  }

  function updateGallery(nextGallery) {
    setForm((prev) => ({
      ...prev,
      gallery: nextGallery
    }));
  }

  function removeGalleryImage(index) {
    updateGallery(form.gallery.filter((_, itemIndex) => itemIndex !== index));
  }

  function moveGalleryImage(index, direction) {
    const nextIndex = index + direction;

    if (nextIndex < 0 || nextIndex >= form.gallery.length) {
      return;
    }

    const nextGallery = [...form.gallery];
    const [moved] = nextGallery.splice(index, 1);
    nextGallery.splice(nextIndex, 0, moved);
    updateGallery(nextGallery);
  }

  function setGalleryCover(index) {
    if (!form.gallery[index]) {
      return;
    }

    const nextGallery = [...form.gallery];
    const [cover] = nextGallery.splice(index, 1);
    nextGallery.unshift(cover);
    updateGallery(nextGallery);
  }

  function assignGalleryAngle(index, label) {
    const slot = ANGLE_ORDER.indexOf(label);

    if (slot === -1 || !form.gallery[index]) {
      return;
    }

    const nextGallery = [...form.gallery];
    const [moved] = nextGallery.splice(index, 1);
    const insertAt = Math.min(slot, nextGallery.length);
    nextGallery.splice(insertAt, 0, moved);
    updateGallery(nextGallery);
  }

  async function permanentlyDeleteProduct(product) {
    setSaving(true);
    setStatus('');

    try {
      const gallery = galleryFromProduct(product);

      if (deleteCloudinaryOnSave && gallery.length) {
        await api.post(
          '/products/media/delete',
          {
            urls: gallery,
            deleteFromCloudinary: true
          },
          token || getAuthToken()
        );
      }

      await api.delete(`/products/${product.id}`, token || getAuthToken());

      setProducts((prev) => prev.filter((item) => item.id !== product.id));

      if (editingId === product.id) {
        setEditingId(null);
      }

      setStatus(`"${product.name}" permanently deleted.`);
    } catch (error) {
      console.error(error);
      setStatus(error.message || 'Failed to delete product');
    } finally {
      setSaving(false);
      closeConfirmModal();
    }
  }

  function requestPermanentDelete(product) {
    setConfirmModal({
      title: 'Permanently delete product?',
      message: `"${product.name}" will be removed from the database. This cannot be undone. Products linked to existing orders may be blocked. Optional Cloudinary cleanup runs if enabled.`,
      confirmLabel: 'Delete permanently',
      destructive: true,
      onConfirm: () => permanentlyDeleteProduct(product)
    });
  }

  async function uploadGalleryFiles(files) {
    if (!files.length) {
      return;
    }

    setUploading(true);
    setStatus('');

    try {
      const uploadedUrls = [];

      for (const file of files) {
        const data = new FormData();
        data.append('image', file);

        const response = await fetch(`${API_BASE}/products/upload`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token || getAuthToken()}`
          },
          body: data
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Image upload failed');
        }

        const imagePath = result.front_image || result.image;

        if (imagePath) {
          uploadedUrls.push(imagePath);
        }
      }

      if (uploadedUrls.length) {
        updateGallery([...(form.gallery || []), ...uploadedUrls]);
      }

      setStatus(
        uploadedUrls.length === 1
          ? 'Image uploaded'
          : `${uploadedUrls.length} images uploaded`
      );
    } catch (error) {
      console.error(error);
      setStatus(error.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function saveProduct(id) {

    const payload = productPayload();
    const previousGallery = [...originalGalleryRef.current];

    setSaving(true);
    setStatus('');

    try {

      await api.patch(
        `/products/${id}`,
        payload,
        token || getAuthToken()
      );

      await cleanupRemovedImages(previousGallery, payload.gallery);

      setEditingId(null);
      originalGalleryRef.current = [];

      setProducts((prev) =>
        prev.map((product) =>
          product.id === id
            ? {
                ...product,
                ...payload,
                front_image: payload.front_image,
                gallery: payload.gallery,
                image: payload.front_image,
                details: {
                  ...(product.details && typeof product.details === 'object'
                    ? product.details
                    : {}),
                  description: payload.description
                }
              }
            : product
        )
      );

      setStatus('Product updated successfully');

    } catch (error) {

      console.error(error);

      setStatus(error.message || 'Failed to save product');

    } finally {

      setSaving(false);

    }

  }

  async function createProduct() {

    if (!form.name.trim()) {
      setStatus('Product name is required.');
      return;
    }

    setSaving(true);
    setStatus('');

    try {

      const created = await api.post('/products', {
        ...productPayload(),
        id: form.id || undefined
      });

      setCreating(false);
      setForm(emptyForm);

      if (created && created.id) {
        setProducts((prev) => [...prev, created]);
      }

      setStatus('Product created successfully');

    } catch (error) {

      console.error(error);
      setStatus(error.message || 'Failed to create product');

    } finally {

      setSaving(false);

    }

  }

  const galleryEditor = (
    <ProductGalleryEditor
      gallery={form.gallery || []}
      uploading={uploading}
      disabled={saving}
      onAddFiles={uploadGalleryFiles}
      onRemove={removeGalleryImage}
      onMove={moveGalleryImage}
      onSetCover={setGalleryCover}
      onAssignAngle={assignGalleryAngle}
    />
  );

  const filteredProducts = products.filter((product) => {
    if (statusFilter === 'active') {
      return product.is_active !== false;
    }

    if (statusFilter === 'hidden') {
      return product.is_active === false;
    }

    return true;
  });

  return (

    <div className="space-y-6">

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Products</h1>
          <p className="mt-1 text-sm text-slate-500">
            Manage cap listings, multi-angle galleries, and storefront visibility.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <select
            className="select-field min-w-[200px]"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="all">All products</option>
            <option value="active">Active on storefront</option>
            <option value="hidden">Hidden from storefront</option>
          </select>

          <button
            onClick={() => {
              setCreating(true);
              setEditingId(null);
              setForm(emptyForm);
              setStatus('');
              originalGalleryRef.current = [];
            }}
            className="btn-accent"
          >
            Add Product
          </button>
        </div>
      </div>

      {loadError ? (
        <div className="status-banner-error">{loadError}</div>
      ) : null}

      {status ? (
        <div className={
          status.toLowerCase().includes('fail') || status.toLowerCase().includes('required')
            ? 'status-banner-error'
            : 'status-banner-success'
        }>
          {status}
        </div>
      ) : null}

      {listLoading ? (
        <div className="card p-6 loading-pulse">Loading products...</div>
      ) : null}

      {!listLoading && !loadError && products.length === 0 ? (
        <div className="rounded-2xl border bg-white p-6 text-slate-500 shadow">
          No products found in the catalog yet.
        </div>
      ) : null}

      {!listLoading && !loadError && products.length > 0 && filteredProducts.length === 0 ? (
        <div className="rounded-2xl border bg-white p-6 text-slate-500 shadow">
          No products match this filter.
        </div>
      ) : null}

      {creating ? (

        <div className="card p-6 md:p-8 space-y-4 animate-fade-up">

          <h2 className="text-xl font-bold">New Product</h2>

          <input className="input-field" placeholder="Product ID (optional, auto-generated from name)" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} />
          <input className="input-field" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className="input-field" placeholder="Short Name" value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} />
          <input className="input-field" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <input className="input-field" placeholder="Badge" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} />
          <textarea className="input-field min-h-[120px]" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <input className="input-field" placeholder="Sizes Example: 7, 7 1/8, 7 1/4" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} />

          {galleryEditor}

          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={deleteCloudinaryOnSave}
              onChange={(event) => setDeleteCloudinaryOnSave(event.target.checked)}
            />
            Delete removed Cloudinary files when saving
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>

          <div className="flex gap-3">
            <button
              onClick={createProduct}
              disabled={saving || uploading}
              className="btn-primary"
            >
              {saving ? 'Creating...' : 'Create Product'}
            </button>

            <button
              onClick={() => {
                setCreating(false);
                setForm(emptyForm);
              }}
              disabled={saving}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>

        </div>

      ) : null}

      <div className="product-list">

        {filteredProducts.map(product => (

          <div
            key={product.id}
            className={`product-card ${
              product.is_active !== false ? 'product-card-active' : 'opacity-85'
            }`}
          >

            {editingId === product.id ? (

              <div className="space-y-4 p-6 md:p-8">

                <input className="input-field" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input className="input-field" placeholder="Short Name" value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} />
                <input className="input-field" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                <input className="input-field" placeholder="Badge" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} />
                <textarea className="input-field min-h-[120px]" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                <input className="input-field" placeholder="Sizes Example: 7, 7 1/8, 7 1/4" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} />

                {galleryEditor}

                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={deleteCloudinaryOnSave}
                    onChange={(event) => setDeleteCloudinaryOnSave(event.target.checked)}
                  />
                  Delete removed Cloudinary files when saving
                </label>

                <label className="flex items-center gap-2">

                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        is_active: e.target.checked
                      })
                    }
                  />

                  Active

                </label>

                <div className="flex flex-col gap-3 sm:flex-row">

                  <button
                    onClick={() =>
                      saveProduct(product.id)
                    }
                    disabled={saving || uploading}
                    className="btn-primary"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>

                  <button
                    onClick={() => {
                      setEditingId(null);
                      originalGalleryRef.current = [];
                    }}
                    disabled={saving}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>

                  {product.is_active === false ? (
                    <button
                      type="button"
                      onClick={() => requestReactivateProduct(product)}
                      disabled={saving}
                      className="btn-accent"
                    >
                      Activate
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => requestDeactivateProduct(product)}
                      disabled={saving}
                      className="btn-danger-soft"
                    >
                      Hide
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => requestPermanentDelete(product)}
                    disabled={saving}
                    className="btn-danger"
                    aria-label={`Permanently delete ${product.name}`}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                    Delete
                  </button>

                </div>

              </div>

            ) : (

              <div className="flex flex-col gap-5 p-5 sm:p-6 md:flex-row md:items-center md:p-8">

                {coverImage(galleryFromProduct(product)) ? (

                  <div className="product-thumb">
                    <img
                      src={resolveAssetUrl(coverImage(galleryFromProduct(product)))}
                      alt={product.name || 'Product'}
                      loading="lazy"
                      decoding="async"
                    />
                    {galleryFromProduct(product).length > 1 ? (
                      <span className="badge-count">
                        {galleryFromProduct(product).length}
                      </span>
                    ) : null}
                  </div>

                ) : null}

                <div className="flex-1">

                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-2xl font-bold">
                      {product.name}
                    </h2>

                    {product.is_active === false ? (
                      <span className="badge badge-red">Hidden</span>
                    ) : (
                      <span className="badge badge-green">Active</span>
                    )}
                  </div>

                  <p>
                    ${product.price}
                  </p>

                  <p>
                    {product.sizes}
                  </p>

                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  <button
                    onClick={() => startEdit(product)}
                    className="btn-accent"
                  >
                    Edit
                  </button>

                  {product.is_active === false ? (
                    <button
                      type="button"
                      onClick={() => requestReactivateProduct(product)}
                      disabled={saving}
                      className="btn-secondary"
                    >
                      Activate
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => requestDeactivateProduct(product)}
                      disabled={saving}
                      className="btn-danger-soft"
                    >
                      Hide
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => requestPermanentDelete(product)}
                    disabled={saving}
                    className="btn-danger"
                    aria-label={`Permanently delete ${product.name}`}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                    Delete
                  </button>
                </div>

              </div>

            )}

          </div>

        ))}

      </div>

      <ConfirmModal
        open={Boolean(confirmModal)}
        title={confirmModal?.title || ''}
        message={confirmModal?.message || ''}
        confirmLabel={confirmModal?.confirmLabel || 'Confirm'}
        destructive={confirmModal?.destructive}
        loading={saving}
        onCancel={closeConfirmModal}
        onConfirm={() => {
          if (confirmModal?.onConfirm) {
            confirmModal.onConfirm();
          }
        }}
      />

    </div>

  );

}